import { CircularProgress, FormControlLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { red } from '@mui/material/colors';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Formsy from 'formsy-react';
import { List, Map, Set, fromJS } from 'immutable';
import _ from 'lodash';
import objectAssign from 'object-assign';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import TextBox from '@sophocles/components/FormFields/TextBox';
import WindowSizeHOC from '@sophocles/components/WindowSizeHOC';
import { fetchAllAuthGroups } from '@sophocles/entities/AuthGroup/authGroupActions';
import { fetchRawCustomer } from '@sophocles/entities/Customer';
import { fetchEligibleSiteGroupsForCustomer } from '@sophocles/entities/User';
import { getAuthUserCustomerSelector, getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { useCreateUserMutation } from '@sophocles/features/entities/users';
import { selectSeparateTenant } from '@sophocles/features/tenant/slice';
import { addToast } from '@sophocles/features/toast';
import { withRouter } from '@sophocles/router/hoc';
import {
  compareAlphanum,
  determineIsAtLeastCustomerAdmin,
  determineIsCustomerAdmin,
  determineIsSiteGroupAdmin,
  windowSizes,
} from '@sophocles/utils';
import { parseApiError } from '@sophocles/utils/apiErrors';
import { determineIsResellerUser } from '@sophocles/utils/userPermissionUtils';
import AuthGroupSelectionTable from '@sophocles/views/users/components/AuthGroupSelectionTable';
import NonStaffAuthGroupSelectionComponent from '@sophocles/views/users/components/NonStaffAuthGroupSelectionComponent';
import SiteGroupSelectionTable from '@sophocles/views/users/components/SiteGroupSelectionTable';

const styles = {
  headline: {
    fontSize: 18,
    marginBottom: 10,
    fontWeight: 500,
  },
};

class CreateUserView extends React.Component {
  constructor(props) {
    super(props);
    const {
      user,
      match: { params },
    } = props;
    const custId = Number.parseInt(params.cust_id) || null;
    const selectedCustomer = custId || user.get('customer');

    this.state = {
      canSubmit: false,
      enableWarning: false,
      disableWarning: false,
      staffSelection: false,
      superUserSelection: false,
      selectedSiteGroups: Set(),
      allSiteGroupUser: false,
      allPossibleSiteGroups: List(),
      userSelectedSiteGroups: List(),
      // Auth group 9 = "User" — most common new-user role, default
      // selection per Portal Cleanup review.
      basicAuthGroup: 9,
      staffAuthGroups: Set([]),
      addonAuthGroups: Set([]),
      errors: {},
      selectedCustomer,
      selectedCustomerMap: Map(),
      fetchingCustomer: false,
    };
  }

  componentDidMount() {
    const { selectedCustomer } = this.state;
    const { fetchAllAuthGroups } = this.props;

    fetchAllAuthGroups();
    this.fetchSiteGroups(selectedCustomer);
    this.fetchCustomer(selectedCustomer);
  }

  selectSiteGroup = item => {
    const { selectedSiteGroups } = this.state;
    return this.setState({ selectedSiteGroups: selectedSiteGroups.add(item.get('id', 0)) });
  };

  unSelectSiteGroup = item => {
    const { selectedSiteGroups } = this.state;
    return this.setState({ selectedSiteGroups: selectedSiteGroups.remove(item.get('id', 0)) });
  };

  toggleAllSiteGroupUser = () => this.setState({ allSiteGroupUser: !this.state.allSiteGroupUser });

  setSelectedSiteGroups = values => this.setState({ selectedSiteGroups: Set(values) });

  fetchCustomer = customer => {
    const { fetchCustomer } = this.props;
    this.setState({ fetchingCustomer: true });
    if (customer) {
      fetchCustomer(customer).then(res => {
        let { basicAuthGroup, selectedCustomerMap } = this.state;

        if (res.error) {
          selectedCustomerMap = Map();
        } else {
          selectedCustomerMap = res;
          if (basicAuthGroup === 1061 && !res.getIn(['options', 'is_reseller'], false)) {
            basicAuthGroup = '';
          }
        }
        return this.setState({
          selectedCustomerMap,
          basicAuthGroup,
          fetchingCustomer: false,
        });
      });
    }
  };

  fetchSiteGroups = customer => {
    const { fetchSiteGroups } = this.props;

    fetchSiteGroups(customer).then(res => {
      const groups = fromJS(res)
        .map(sg => sg.set('selectable', true))
        .toMap()
        .mapKeys((key, val) => val.get('id', 0));

      this.setState({ allPossibleSiteGroups: groups.toList() });
    });
  };

  submitForm = () => this.form.submit();

  createUser = async formValues => {
    const {
      staffAuthGroups,
      selectedCustomer,
      basicAuthGroup,
      staffSelection,
      superUserSelection,
      allSiteGroupUser,
      selectedSiteGroups,
      addonAuthGroups,
    } = this.state;

    const { user, createUser, router, location, addToast, t, separateTenant } = this.props;

    const values = {
      customer_id: selectedCustomer,
      is_staff: false,
      is_superuser: false,
      group_ids: [],
      all_sitegroups_user: allSiteGroupUser,
      first_name: formValues.first_name,
      last_name: formValues.last_name,
      email: formValues.email,
      is_active: true,
    };

    // determine staff / super user
    if (selectedCustomer === 1) {
      values.is_staff = staffSelection;
      if (staffSelection) {
        values.all_sitegroups_user = true;
      }
      if (user.get('is_superuser', false)) {
        values.is_superuser = staffSelection && superUserSelection;
      }
    }

    // set auth groups — for a staff user on the ODL customer (#1) in
    // separate-tenant the role/group subselection is hidden and the backend
    // derives groups from is_staff, so group_ids is omitted. A non-staff user
    // on #1 (and every other customer) keeps the normal role selection.
    if (separateTenant && selectedCustomer === 1 && staffSelection) {
      delete values.group_ids;
    } else if (selectedCustomer !== 1 || !staffSelection) {
      values.group_ids = [basicAuthGroup];
      if (!addonAuthGroups.isEmpty()) {
        values.group_ids = values.group_ids.concat(addonAuthGroups.toArray());
      }
    } else {
      values.group_ids = staffAuthGroups.toArray();
    }

    // set site groups
    if (values.all_sitegroups_user) {
      // values.site_group_ids = [];
    } else {
      values.site_group_ids = selectedSiteGroups.toArray();
    }

    // make sure we end in users/

    try {
      const created = await createUser({ body: values }).unwrap();
      addToast({
        title: t('general.success', 'Success'),
        message: t(
          'userSettings.userCreatedSuccess',
          'User created successfully. A welcome email with password instructions has been sent to the given email address.',
        ),
        level: 'success',
        dismissible: true,
        autoDismiss: 20,
      });
      const pathname = location.pathname.split('new')[0];
      router.navigate(pathname + created.id, { replace: true });
    } catch (error) {
      const { fieldErrors, nonFieldMessages } = parseApiError(error, {
        knownFields: ['email'],
        genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
      });
      this.handleErrors(fieldErrors.email ? { email: fieldErrors.email.join(' ') } : {});

      if (nonFieldMessages.length > 0) {
        addToast({
          title: t('general.error', 'Error'),
          message: nonFieldMessages.join('; '),
          level: 'error',
          dismissible: true,
        });
      }
    }
  };

  clearEmailError = () => {
    const newErrors = objectAssign({}, this.state.errors);
    delete newErrors.email;
    this.setState({ errors: newErrors });
  };

  handleErrors = errors => this.setState({ errors });

  enableSubmit = () => this.setState({ canSubmit: true });

  disableSubmit = () => this.setState({ canSubmit: false });

  setBasicAuthGroup = value => this.setState({ basicAuthGroup: value });

  toggleStaffSelection = () => this.setState({ staffSelection: !this.state.staffSelection });

  toggleSuperUserSelection = () => this.setState({ superUserSelection: !this.state.superUserSelection });

  setSelectedCustomer = val => {
    const { selectedCustomer, selectedSiteGroups } = this.state;

    const newSelectedSiteGroups = val !== selectedCustomer ? Set([]) : selectedSiteGroups;
    this.fetchCustomer(val);
    if (val) {
      this.fetchSiteGroups(val);
    }
    this.setState({ selectedCustomer: val, selectedSiteGroups: newSelectedSiteGroups });
    if (val !== 1) {
      this.setState({ staffSelection: false, superUserSelection: false });
    }
  };

  setSelectedStaffAuthGroups = groups => this.setState({ staffAuthGroups: Set(groups) });

  setAddonAuthGroups = addonAuthGroups => this.setState({ addonAuthGroups });

  render() {
    const { user, customer, windowWidth, t, separateTenant } = this.props;

    const {
      staffAuthGroups,
      selectedCustomer,
      basicAuthGroup,
      staffSelection,
      superUserSelection,
      canSubmit,
      allSiteGroupUser,
      selectedSiteGroups,
      allPossibleSiteGroups,
      addonAuthGroups,
      selectedCustomerMap,
      fetchingCustomer,
    } = this.state;

    const onMobile = windowWidth <= windowSizes.tablet.width;

    // const isCurrentSuperUser = currentUser.get('is_superuser', false);

    const pageTitle = t('userSettings.newUserAccount');

    const groups = user.get('groups', []);

    const isStaff = user.get('is_staff', false);
    const isSuper = user.get('is_superuser', false);
    const isBillboard = customer.get('is_billboard', false);
    const canCreateResellers =
      selectedCustomerMap.getIn(['options', 'is_reseller'], false) && (isStaff || determineIsResellerUser(groups));
    const canChangeCustomer = isStaff || determineIsResellerUser(groups);

    const isCustomerAdmin = isStaff || determineIsAtLeastCustomerAdmin(groups);
    const isSiteGroupAdmin = isCustomerAdmin || determineIsSiteGroupAdmin(groups);

    // A staff user on the ODL customer (#1) in separate-tenant hides the
    // role/group subselection (staff auto-implies roles server-side), so
    // there's no group choice to validate; a non-staff user on #1 and every
    // other customer selects a role as normal.
    const authSelected =
      separateTenant && selectedCustomer === 1 && staffSelection
        ? true
        : staffSelection
          ? !staffAuthGroups.isEmpty()
          : basicAuthGroup && basicAuthGroup !== '';
    const plantSelected = allSiteGroupUser || !selectedSiteGroups.isEmpty();

    const sortedData = allPossibleSiteGroups.sortBy(item => item.get('name', 'id'), compareAlphanum);

    const selectedData = sortedData.filter(group => selectedSiteGroups.contains(group.get('id', 0)));
    const unSelectedData = sortedData.filterNot(group => selectedSiteGroups.contains(group.get('id', 0)));

    const canSetAllSiteGroups =
      user.get('all_sitegroups_user', false) || determineIsCustomerAdmin(user.get('groups', []));

    const emailError = _.get(this.state.errors, 'email', false);

    return (
      <AppNav page="support_users" notFullHeight={true} {...this.props}>
        <div className="container-fluid" style={{ top: '64px', height: '100%', margin: '0 16px' }}>
          <TitleBar showBackButton title={pageTitle} />

          <Paper style={{ marginBottom: '16px' }}>
            <div style={{ padding: '0 16px' }}>
              <Formsy
                ref={input => {
                  this.form = input;
                }}
                onSubmit={this.createUser}
                onValid={this.enableSubmit}
                onInvalid={this.disableSubmit}
                className=""
              >
                <div style={{ marginBottom: '16px', paddingTop: '16px' }}>
                  <div style={{ display: onMobile ? 'block' : 'flex' }}>
                    <div style={{ flex: '0 0 250px' }}>
                      <h4 style={styles.headline}>{t('userSettings.personalSettingsHeading')}</h4>
                      <div>
                        <p>{t('userSettings.personalSettingsDescription')}</p>
                      </div>
                    </div>

                    <div style={{ flex: '1 1 auto', paddingLeft: onMobile ? 0 : '16px' }}>
                      <TextBox
                        value={''}
                        name="first_name"
                        title={t('general.firstName')}
                        type="text"
                        required={true}
                        validations={{ maxLength: 20 }}
                        validationErrors={{ maxLength: t('userSettings.maxCharacters', { count: 20 }) }}
                        fullWidth
                      />
                      <TextBox
                        value={''}
                        name="last_name"
                        title={t('general.lastName')}
                        type="text"
                        validations={{ maxLength: 20 }}
                        validationErrors={{ maxLength: t('userSettings.maxCharacters', { count: 20 }) }}
                        fullWidth
                      />
                      <TextBox
                        value={''}
                        name="email"
                        title={t('general.emailAddress')}
                        type="email"
                        passValue={this.clearEmailError}
                        error={emailError}
                        required={true}
                        validations={{
                          isEmail: true,
                          maxLength: 254,
                        }}
                        validationErrors={{
                          isEmail: t('userSettings.validEmail'),
                          maxLength: t('userSettings.maxCharacters', { count: 254 }),
                        }}
                        fullWidth={true}
                      />
                    </div>
                  </div>
                </div>

                <Divider />

                {canChangeCustomer && (
                  <div style={{ marginBottom: '16px', paddingTop: '16px' }}>
                    <div style={{ display: onMobile ? 'block' : 'flex' }}>
                      <div style={{ flex: '0 0 250px' }}>
                        <h4 style={styles.headline}>{t('userSettings.customerHeading')}</h4>
                        <div>* {t('userSettings.adminsOnly')} </div>
                      </div>

                      <div style={{ flex: '1 1 auto', paddingLeft: '16px' }}>
                        <CustomerAutocomplete
                          fullWidth
                          value={selectedCustomer || null}
                          onChange={(e, customerId) => this.setSelectedCustomer(customerId)}
                        />
                        {!selectedCustomer && (
                          <div style={{ color: red[500] }}> {t('userSettings.mustSelectCustomer')}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {isStaff && <Divider />}

                <div style={{ marginBottom: '16px', paddingTop: '16px' }}>
                  <div style={{ display: onMobile ? 'block' : 'flex' }}>
                    <div style={{ flex: '0 0 250px' }}>
                      <h4 style={styles.headline}>{t('userSettings.roleHeading')}</h4>
                      <div>{t('userSettings.roleHeadingDescription')}</div>
                    </div>

                    <div style={{ flex: '1 1 auto', paddingLeft: onMobile ? 0 : '16px' }}>
                      {isStaff && selectedCustomer === 1 && (
                        <FormControlLabel
                          label={t('userSettings.staff', 'Staff')}
                          control={
                            <Checkbox
                              style={{ width: 'auto' }}
                              onChange={this.toggleStaffSelection}
                              checked={staffSelection}
                            />
                          }
                        />
                      )}
                      {isSuper && selectedCustomer === 1 && staffSelection && (
                        <FormControlLabel
                          label={t('users.superUserLabel', 'Super User (Please only Engineering')}
                          control={
                            <Checkbox
                              style={{ width: 'auto' }}
                              onChange={this.toggleSuperUserSelection}
                              checked={superUserSelection}
                            />
                          }
                        />
                      )}
                      {!(separateTenant && selectedCustomer === 1 && staffSelection) &&
                        (selectedCustomer === 1 && isStaff && staffSelection ? (
                          <div>
                            <AuthGroupSelectionTable
                              selected={staffAuthGroups}
                              user={user}
                              customer={selectedCustomer}
                              saveValues={this.setSelectedStaffAuthGroups}
                            />
                          </div>
                        ) : (
                          <NonStaffAuthGroupSelectionComponent
                            setMainAuthGroup={this.setBasicAuthGroup}
                            mainAuthGroup={basicAuthGroup}
                            canCreateAdmins={isCustomerAdmin}
                            canCreateManagers={isSiteGroupAdmin}
                            canCreateResellers={canCreateResellers}
                            isBillboard={isBillboard}
                            addonAuthGroups={addonAuthGroups}
                            setAddonAuthGroups={this.setAddonAuthGroups}
                            onMobile={onMobile}
                          />
                        ))}
                    </div>
                  </div>
                </div>

                {!staffSelection && <Divider />}

                {!staffSelection && (
                  <div style={{ marginBottom: '16px', paddingTop: '16px' }}>
                    <div style={{ display: onMobile ? 'block' : 'flex' }}>
                      <div style={{ flex: '0 0 250px' }}>
                        <h4 style={styles.headline}>
                          {isBillboard ? t('userSettings.plantAccess') : t('userSettings.siteGroupAccess')}
                        </h4>
                        <div>
                          {t('userSettings.accessDescription', {
                            entityType: isBillboard
                              ? t('siteGroups.plants').toLowerCase()
                              : t('siteGroups.siteGroups').toLowerCase(),
                          })}
                        </div>
                      </div>

                      <div style={{ flex: '1 1 auto', paddingLeft: onMobile ? 0 : '16px' }}>
                        {canSetAllSiteGroups && (
                          <div style={{ padding: '4px 0', width: '300px' }}>
                            <FormControlLabel
                              label={
                                isBillboard ? t('userSettings.accessAllPlants') : t('userSettings.accessAllSiteGroups')
                              }
                              control={<Checkbox onChange={this.toggleAllSiteGroupUser} checked={allSiteGroupUser} />}
                            />
                          </div>
                        )}

                        <div>
                          {selectedCustomer && !allSiteGroupUser && (
                            <div>
                              <div style={{ paddingBottom: '10px' }}>
                                {isBillboard
                                  ? t('userSettings.chooseSpecificPlants')
                                  : t('userSettings.chooseSpecificSiteGroups')}{' '}
                                {selectedSiteGroups.isEmpty() && (
                                  <span style={{ color: red[500] }}>{t('userSettings.mustSelectAtLeastOne')}</span>
                                )}
                              </div>
                              <SiteGroupSelectionTable
                                containerStyle={{ display: onMobile ? 'block' : 'flex' }}
                                isBillboard={isBillboard}
                                customer={selectedCustomer}
                                selected={selectedSiteGroups}
                                user={user}
                                saveValues={this.setSelectedSiteGroups}
                                selectData={this.selectSiteGroup}
                                unSelectData={this.unSelectSiteGroup}
                                selectedData={selectedData}
                                unSelectedData={unSelectedData}
                                granted={selectedData}
                                revoked={Set()}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ padding: '16px 0' }}>
                  <div style={{ display: onMobile ? 'block' : 'flex' }}>
                    <div style={{ flex: '0 0 250px' }} />
                    <div style={{ flex: '1 1 auto', paddingLeft: '16px' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={this.submitForm}
                        disabled={
                          !canSubmit ||
                          !authSelected ||
                          !selectedCustomer ||
                          (!plantSelected && !staffSelection) ||
                          !!emailError
                        }
                      >
                        {t('userSettings.createUserAccount')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Formsy>
            </div>
          </Paper>
        </div>
      </AppNav>
    );
  }
}

function mapStateToProps(state) {
  return {
    user: getAuthUserSelector(state),
    customer: getAuthUserCustomerSelector(state),
    separateTenant: selectSeparateTenant(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchAllAuthGroups: () => dispatch(fetchAllAuthGroups()),
    fetchSiteGroups: customer => dispatch(fetchEligibleSiteGroupsForCustomer(customer)),
    fetchCustomer: customer => dispatch(fetchRawCustomer(customer)),
    addToast: toast => dispatch(addToast(toast)),
  };
}

// Thin functional bridge: supplies the odl-app createUser trigger as a prop with
// the legacy thunk's call shape so the connected class body stays untouched.
function CreateUserViewWithUserMutations(props) {
  const [createUser] = useCreateUserMutation();
  return <CreateUserView {...props} createUser={createUser} />;
}

const CreateUserViewContainer = withRouter(
  withTranslation()(connect(mapStateToProps, mapDispatchToProps)(CreateUserViewWithUserMutations)),
);

export default WindowSizeHOC(CreateUserViewContainer);
